import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, MapPin, Search, X } from 'lucide-react'
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api'
import api from '../../services/api'
import { formatPhone } from '../../utils/formatters'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import toast from 'react-hot-toast'

// Campos de endereço afetados pelo checkbox "Solicitar endereço do destinatário"
const ADDRESS_FIELDS = [
  'addressStreet',
  'addressNumber',
  'addressComplement',
  'addressNeighborhood',
  'addressCity',
  'addressState',
  'addressZipcode',
]

// Schema de validação
// Os campos de endereço não têm regra fixa de obrigatoriedade: quando
// requestRecipientAddress é true, o destinatário preenche depois (link
// próprio), então a validação inteira desses campos é pulada no superRefine.
const recipientSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').min(2, 'Nome muito curto'),
  phone: z.string().min(1, 'Telefone é obrigatório').transform(val => val.replace(/\D/g, '')).pipe(z.string().regex(/^\d{10,11}$/, 'Telefone inválido (10 ou 11 dígitos com DDD)')),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  requestRecipientAddress: z.boolean().default(false),
  addressStreet: z.string(),
  addressNumber: z.string(),
  addressComplement: z.string().optional(),
  addressNeighborhood: z.string(),
  addressCity: z.string(),
  addressState: z.string(),
  addressZipcode: z.string().transform(val => val.replace(/\D/g, '')),
  notes: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.requestRecipientAddress) {
    return
  }
  if (!data.addressStreet) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['addressStreet'], message: 'Rua é obrigatória' })
  }
  if (!data.addressNumber) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['addressNumber'], message: 'Número é obrigatório' })
  }
  if (!data.addressNeighborhood) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['addressNeighborhood'], message: 'Bairro é obrigatório' })
  }
  if (!data.addressCity) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['addressCity'], message: 'Cidade é obrigatória' })
  }
  if (data.addressState.length < 2) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['addressState'], message: 'Estado é obrigatório' })
  } else if (data.addressState.length > 2) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['addressState'], message: 'Use a sigla' })
  }
  if (data.addressZipcode.length !== 8) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['addressZipcode'], message: 'CEP deve ter 8 dígitos' })
  }
})

// Configuração do mapa
const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px'
}

// Centro padrão: Manaus
const defaultCenter = {
  lat: -3.1190,
  lng: -60.0217
}

export default function RecipientForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditing = !!id

  // Estados para o mapa
  const [showMap, setShowMap] = useState(false)
  const [coordinates, setCoordinates] = useState(null)
  const [isGeocoding, setIsGeocoding] = useState(false)

  // Carrega a API do Google Maps
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
  })

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(recipientSchema),
    defaultValues: {
      requestRecipientAddress: false,
      addressState: 'AM',
      addressCity: 'Manaus',
    }
  })

  const requestRecipientAddress = watch('requestRecipientAddress')

  // Marcar ou desmarcar sempre limpa os 7 campos de endereço (sem restaurar
  // valor anterior) e derruba qualquer erro de validação preso neles - evita
  // mensagem de erro pendurada embaixo de campo desabilitado.
  const handleRequestAddressToggle = (e) => {
    const checked = e.target.checked
    setValue('requestRecipientAddress', checked)
    ADDRESS_FIELDS.forEach((field) => {
      setValue(field, '')
      clearErrors(field)
    })
  }

  // Busca dados se estiver editando
  const { data: recipient, isLoading } = useQuery({
    queryKey: ['recipient', id],
    queryFn: async () => {
      const response = await api.get(`/api/recipients/${id}`)
      return response.data.data.recipient
    },
    enabled: isEditing,
  })

  // Preenche form com dados existentes
  useEffect(() => {
    if (recipient) {
      // Mapear campos do backend para o formulário
      const formData = {
        name: recipient.name,
        phone: formatPhone(recipient.phone),
        email: recipient.email || '',
        addressStreet: recipient.address?.street || recipient.addressStreet || '',
        addressNumber: recipient.address?.number || recipient.addressNumber || '',
        addressComplement: recipient.address?.complement || recipient.addressComplement || '',
        addressNeighborhood: recipient.address?.neighborhood || recipient.addressNeighborhood || '',
        addressCity: recipient.address?.city || recipient.addressCity || 'Manaus',
        addressState: recipient.address?.state || recipient.addressState || 'AM',
        addressZipcode: recipient.address?.zipcode || recipient.addressZipcode || '',
        notes: recipient.notes || '',
      }
      reset(formData)
      
      // Se tem coordenadas, mostra no mapa
      if (recipient.coordinates?.lat && recipient.coordinates?.lng) {
        setCoordinates({
          lat: recipient.coordinates.lat,
          lng: recipient.coordinates.lng
        })
        setShowMap(true)
      }
    }
  }, [recipient, reset])

  // Função para geocodificar o endereço
  const handleGeocode = async () => {
    const values = getValues()
    
    if (!values.addressStreet || !values.addressNumber || !values.addressNeighborhood) {
      toast.error('Preencha rua, número e bairro primeiro')
      return
    }

    setIsGeocoding(true)

    try {
      const fullAddress = `${values.addressStreet}, ${values.addressNumber} - ${values.addressNeighborhood}, ${values.addressCity}/${values.addressState}, Brasil`
      
      const response = await api.post('/api/recipients/geocode', {
        address: fullAddress
      })

      if (response.data.data?.success && response.data.data?.lat) {
        setCoordinates({
          lat: response.data.data.lat,
          lng: response.data.data.lng
        })
        setShowMap(true)
        toast.success('Localização encontrada! Ajuste o pin se necessário.')
      } else {
        toast.error('Endereço não encontrado. Verifique os dados.')
      }
    } catch (error) {
      toast.error('Erro ao buscar localização')
      console.error('Geocode error:', error)
    } finally {
      setIsGeocoding(false)
    }
  }

  // Callback quando o marcador é arrastado
  const onMarkerDragEnd = useCallback((e) => {
    const newLat = e.latLng.lat()
    const newLng = e.latLng.lng()
    setCoordinates({ lat: newLat, lng: newLng })
    toast.success('Localização ajustada!')
  }, [])

  // Mutation para criar/atualizar
  const mutation = useMutation({
    mutationFn: async (data) => {
      if (isEditing) {
        return api.put(`/api/recipients/${id}`, data)
      }
      return api.post('/api/recipients', data)
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Destinatário atualizado!' : 'Destinatário criado!')
      queryClient.invalidateQueries({ queryKey: ['recipients'] })
      if (isEditing) {
        queryClient.invalidateQueries({ queryKey: ['recipient', id] })
        queryClient.invalidateQueries({ queryKey: ['deliveries'] })
        queryClient.invalidateQueries({ queryKey: ['availableDeliveries'] })
      }
      navigate('/recipients')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar')
    },
  })

  const onSubmit = (data) => {
    // Transforma para o formato que o backend espera
    const payload = {
      name: data.name,
      phone: data.phone,
      email: data.email || undefined,
      notes: data.notes || undefined,
      // Inclui coordenadas se foram definidas
      lat: coordinates?.lat || undefined,
      lng: coordinates?.lng || undefined,
    }

    // requestRecipientAddress só existe no schema de criação - em edição o
    // checkbox nem aparece (isEditing), não faz sentido mandar o campo no PUT.
    if (!isEditing) {
      payload.requestRecipientAddress = data.requestRecipientAddress
    }

    // Backend rejeita com 400 se a chave address existir quando
    // requestRecipientAddress é true (Joi.forbidden) - nem manda vazia.
    if (!data.requestRecipientAddress) {
      payload.address = {
        street: data.addressStreet,
        number: data.addressNumber,
        complement: data.addressComplement || undefined,
        neighborhood: data.addressNeighborhood,
        city: data.addressCity,
        state: data.addressState,
        zipcode: data.addressZipcode,
      }
    }

    mutation.mutate(payload)
  }

  if (isEditing && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate('/recipients')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="page-title">
            {isEditing ? 'Editar Destinatário' : 'Novo Destinatário'}
          </h1>
          <p className="page-subtitle">
            {isEditing ? 'Atualize os dados do destinatário.' : 'Cadastre um novo cliente para entregas.'}
          </p>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Dados pessoais */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">Dados do Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome completo"
                placeholder="Nome do destinatário"
                error={errors.name?.message}
                {...register('name')}
              />
              <Input
                label="Telefone (WhatsApp)"
                placeholder="(92) 99999-8888"
                error={errors.phone?.message}
                {...register('phone')}
              />
              <Input
                label="Email (opcional)"
                type="email"
                placeholder="email@exemplo.com"
                error={errors.email?.message}
                {...register('email')}
              />
            </div>
          </div>

          {/* Solicitar endereço do destinatário */}
          {!isEditing && (
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!requestRecipientAddress}
                  onChange={handleRequestAddressToggle}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-600">Solicitar endereço do destinatário</span>
              </label>
            </div>
          )}

          {/* Endereço */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">Endereço de Entrega</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="CEP"
                placeholder="69000-000"
                error={errors.addressZipcode?.message}
                disabled={!!requestRecipientAddress}
                {...register('addressZipcode')}
              />
              <div></div>

              <Input
                label="Rua"
                placeholder="Nome da rua"
                error={errors.addressStreet?.message}
                disabled={!!requestRecipientAddress}
                {...register('addressStreet')}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Número"
                  placeholder="123"
                  error={errors.addressNumber?.message}
                  disabled={!!requestRecipientAddress}
                  {...register('addressNumber')}
                />
                <Input
                  label="Complemento"
                  placeholder="Apto, etc."
                  disabled={!!requestRecipientAddress}
                  {...register('addressComplement')}
                />
              </div>

              <Input
                label="Bairro"
                placeholder="Nome do bairro"
                error={errors.addressNeighborhood?.message}
                disabled={!!requestRecipientAddress}
                {...register('addressNeighborhood')}
              />
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Input
                    label="Cidade"
                    placeholder="Manaus"
                    error={errors.addressCity?.message}
                    disabled={!!requestRecipientAddress}
                    {...register('addressCity')}
                  />
                </div>
                <Input
                  label="UF"
                  placeholder="AM"
                  maxLength={2}
                  error={errors.addressState?.message}
                  disabled={!!requestRecipientAddress}
                  {...register('addressState')}
                />
              </div>
            </div>

            {/* Botão para buscar no mapa */}
            <div className="mt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleGeocode}
                isLoading={isGeocoding}
                className="w-full md:w-auto"
              >
                <Search className="w-4 h-4" />
                {coordinates ? 'Buscar novamente' : 'Buscar no mapa'}
              </Button>
              
              {coordinates && (
                <span className="ml-3 text-sm text-green-600">
                  <MapPin className="w-4 h-4 inline" /> Localização definida
                </span>
              )}
            </div>

            {/* Mapa */}
            {showMap && isLoaded && coordinates && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">
                    Arraste o pin para ajustar a localização exata
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowMap(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={coordinates}
                  zoom={17}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  <Marker
                    position={coordinates}
                    draggable={true}
                    onDragEnd={onMarkerDragEnd}
                  />
                </GoogleMap>
                <p className="text-xs text-gray-500 mt-2">
                  Coordenadas: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
                </p>
              </div>
            )}
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações (opcional)
            </label>
            <textarea
              placeholder="Informações adicionais sobre entregas para este destinatário..."
              rows={3}
              className="input-field"
              {...register('notes')}
            />
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="secondary"
              onClick={() => navigate('/recipients')}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              isLoading={mutation.isPending}
            >
              {isEditing ? 'Salvar alterações' : 'Cadastrar'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}